/**
 * The closed CSS value sets that the email/document renderer writes VERBATIM
 * into sent HTML — either into an inline `style="…"` attribute or, for
 * `tagStyle`, into a TAG POSITION:
 *
 *   `<${tagStyle} … style="…">` / `</${tagStyle}>`   ← HEADING_LEVELS
 *   `font-family: ${fontType || globalFont};`        ← isSafeFontStack
 *   `font-size: ${fontSize}px;`                      ← normalizeFontSize
 *   `text-transform: ${textTransform};`              ← TEXT_TRANSFORMS
 *   `text-align: ${textAlign};`                      ← ALIGNS
 *
 * (Deliberately without line numbers — the previous revision carried five that
 * had all drifted, which is worse than none.)
 *
 * There is NO final sanitize pass over the assembled document — DOMPurify runs
 * only over each text value (parse-email.ts:68) — so any value that reaches a
 * prop reaches the recipient's inbox unchanged. Every writer that can put a
 * MODEL-supplied value into one of these props must gate it here.
 *
 * POLICY — normalize what is unambiguously the same value, hard-error what is
 * present but not normalizable, and NEVER silently write a value nobody asked
 * for. Absent (`undefined` / `null` / `""`) is never an error. Clamp only where
 * the clamped value still reads as the author's intent: 9999px → 200px is "as
 * big as possible", 0px → 1px is invisible text, i.e. a broken artifact.
 *
 * The split is WRITE vs READ, not compiler vs edit-op (an earlier revision
 * claimed a compile error discards the whole canvas — it does not; all five call
 * sites return before any tree mutation, so failing costs a retry, nothing
 * else). The write lane normalizes and errors, because its input came from a
 * model that can try again. The read lane (`craftJsToBlocks`) DROPS, because its
 * input came from humans, presets and legacy templates — a read that failed
 * would make `read_*_canvas` unusable on exactly the canvases the model most
 * needs to see, and no retry can fix that.
 *
 * The invariant tying them together: WHATEVER THE READER REPORTS, THE WRITER
 * ACCEPTS. It is a rule to CHECK against the corpus, not a property that falls
 * out of the types — an earlier revision claimed it held "by construction" and
 * it did not: the reader had no lower bound on `lineHeight` while the writer
 * rejected below 0.5, and a shipped preset sets 0.4, so reading that canvas and
 * echoing it back failed forever. Every bound here is set from what the corpus
 * actually holds. It is also why a closed set must contain every value the
 * PRODUCT can produce (see `justify` on ALIGNS) and why an out-of-range font
 * size clamps rather than erroring: the reader reports it verbatim.
 *
 * Dependency-free by contract: imported by the A4 builder settings panels and
 * child prop types (React) AND by buildCraftJsFromBlocks / craftJsToBlocks /
 * builderEditOps, which are documented React-free. `src/lib/utils/` is the only
 * layer both sides may import — A4Builder is the LOWER component layer and must
 * never import from PCreateMailTemplate.
 */

export const TEXT_TRANSFORMS = [
  "none",
  "uppercase",
  "lowercase",
  "capitalize",
] as const;
export type TextTransform = (typeof TEXT_TRANSFORMS)[number];

/**
 * Italic, as a real prop.
 *
 * 98 preset nodes across the press and dinner families already set `fontStyle`
 * — and NOTHING read it: it was absent from all 16 child components, all 16
 * settings panels and the serializer. The whole editorial-italic identity of
 * those templates rendered upright.
 */
export const FONT_STYLES = ["normal", "italic"] as const;
export type FontStyle = (typeof FONT_STYLES)[number];
export const isFontStyle = (v: unknown): v is FontStyle =>
  typeof v === "string" && (FONT_STYLES as readonly string[]).includes(v);

/**
 * `justify` is here because the PRODUCT produces it: the inline text toolbar
 * (`TextEditingSettingsA4.tsx`) and the Button/Divider/Social align dropdowns
 * all offer it, and `Title.tsx` types `textAlign` with it. A narrower set is a
 * dead end rather than a guard — the reader would not report a hand-justified
 * paragraph, so the model could emit nothing that preserves it, and the next
 * `replace` would silently centre it.
 */
export const ALIGNS = ["left", "center", "right", "justify"] as const;
export type Align = (typeof ALIGNS)[number];

/**
 * Alignment for the MEDIA blocks. `justify` is missing on purpose: an image, a
 * GIF, a sticker or a QR code is a single inline-block child, and a lone child
 * sits on the last line — which is never justified. So it renders LEFT while
 * the reader would report "justify" back, i.e. it looks applied and is not.
 * Their settings panels do not offer it either.
 */
export const MEDIA_ALIGNS = ["left", "center", "right"] as const;
export type MediaAlign = (typeof MEDIA_ALIGNS)[number];

export const HEADING_LEVELS = ["h1", "h2", "h3"] as const;
export type HeadingLevel = (typeof HEADING_LEVELS)[number];

/**
 * Where a document sheet's content sits on a page taller than the content.
 * Reaches the output the same way the sets above do —
 * `parse-email.ts` parseBody writes `vertical-align: ${…}` into the wrapper
 * cell's inline style — so a model-supplied value has to be gated here too.
 *
 * "top" is the LEGACY FLOW and is stored as an ABSENT prop, never as the
 * string: the editor deletes the prop when the user picks top, and both the
 * canvas (`Page.tsx`) and the renderer treat missing as top. Writers must keep
 * that convention or every untouched document stops serializing byte-identically.
 */
export const VERTICAL_ALIGNS = ["top", "center", "bottom"] as const;
export type VerticalAlign = (typeof VERTICAL_ALIGNS)[number];

export const isTextTransform = (v: unknown): v is TextTransform =>
  typeof v === "string" && (TEXT_TRANSFORMS as readonly string[]).includes(v);

export const isAlign = (v: unknown): v is Align =>
  typeof v === "string" && (ALIGNS as readonly string[]).includes(v);

/**
 * The READ-lane twin of `normalizeMediaAlign`.
 *
 * It exists because the two lanes drifted apart: the writer was narrowed to
 * MEDIA_ALIGNS while the reader still reported the full set, so an image whose
 * `align` had become "justify" was handed to the model and then rejected by its
 * very next apply — with the whole canvas, not just that block. That is the
 * read/write invariant this file's header calls load-bearing, so the media
 * blocks need a narrow predicate on BOTH sides.
 */
export const isMediaAlign = (v: unknown): v is MediaAlign =>
  typeof v === "string" && (MEDIA_ALIGNS as readonly string[]).includes(v);

export const isVerticalAlign = (v: unknown): v is VerticalAlign =>
  typeof v === "string" && (VERTICAL_ALIGNS as readonly string[]).includes(v);

export const isHeadingLevel = (v: unknown): v is HeadingLevel =>
  typeof v === "string" && (HEADING_LEVELS as readonly string[]).includes(v);

/** The longest stack in the corpus is 74 chars ("'Neue Haas Grotesk', …"). */
export const MAX_FONT_STACK = 100;

/**
 * A CSS font-family value is an OPEN string: `fontType` holds both bare names
 * from the settings dropdown ("georgia", "ヒラギノ角ゴ pro w3") and full stacks
 * from the design directions ("'Bodoni 72', 'Bodoni MT', Georgia, serif" —
 * presets/_helpers.ts:308/330). So this is a CHARACTER allow-list, not a name
 * list. Excluded is exactly what breaking out of `style="…"` needs: `"` `;`
 * `<` `>` `/` `=` `(` `)` `{` `}` `\` `:` and newlines. The corpus quotes with
 * `'` only, never `"`, so excluding `"` costs nothing.
 */
const FONT_STACK_RE = /^[\p{L}\p{M}\p{N} ,'-]+$/u;

/**
 * The apostrophe count must be EVEN. `font-family: a';` leaves an unterminated
 * CSS string that swallows every declaration parse-email emits after it —
 * font-size, font-weight, letter-spacing, text-transform, text-align all
 * silently vanish. Not an escape, but a model-triggerable rendering defect.
 */
/**
 * TRIMMED before testing, because `normalizeFontStack` trims too and the two
 * must agree — this predicate is the READ gate and that one is the WRITE gate.
 * Untrimmed, `fontType: " "` passed here (the space is in the allow-list), so
 * the reader handed the model a stack whose very next `apply` came back
 * `invalidFont` — the same read/write invariant break that `lineHeight`'s floor
 * had. The length cap applies to the trimmed value for the same reason.
 */
export const isSafeFontStack = (v: unknown): v is string => {
  if (typeof v !== "string") return false;
  const s = v.trim();
  return (
    s.length <= MAX_FONT_STACK &&
    FONT_STACK_RE.test(s) &&
    (s.match(/'/g)?.length ?? 0) % 2 === 0
  );
};

/**
 * The absolute leading `parseParagraph` has always pinned Outlook to when the
 * author chose the "default" line-height option (the `lineHeight: 0` sentinel).
 *
 * Word ignores `line-height` and needs `mso-line-height-alt` in px, so SOME
 * number has to be named here; 1.2 is the one that has shipped. That is the
 * whole claim. It is deliberately NOT "what the recipient sees" and NOT a
 * default for anything else: `line-height: normal` is a font metric
 * (ascent+descent+lineGap ÷ upm), so it differs per stack, and several faces
 * the builder offers sit well above 1.2. Every other renderer resolves the
 * sentinel itself — the serializer emits no `line-height` at all for it.
 */
export const OUTLOOK_FALLBACK_LINE_HEIGHT = 1.2;

/**
 * The leading a DOCUMENT sheet's text is measured at — a geometry lock, not a
 * design choice.
 *
 * `DocumentEditorBody` paginates by reading each row's `offsetHeight` off the
 * live canvas, and every stored document's page breaks were computed while
 * these nodes rendered at 1.5 (inherited from Tailwind preflight's
 * `html { line-height: 1.5 }` — nothing between `html` and the text node
 * overrides it, `.h1`/`.body-*` never apply). Change this number and the whole
 * stored corpus repaginates on open: rows migrate between sheets, pages can
 * disappear, and it is neither flagged dirty nor undoable, because the reflow
 * clears its own history.
 *
 * So it is stated EXPLICITLY rather than left to inheritance — a Tailwind major
 * or moving the canvas into an iframe would otherwise move it silently, and a
 * test asserting "no declaration" cannot tell the guard from its own absence.
 *
 * That the editor thus measures more generously than openhtmltopdf renders is a
 * known, separate inaccuracy: correcting it means repaginating the corpus and
 * re-tuning `SAFETY_MARGIN` together, behind a migration.
 */
export const LEGACY_PAGINATION_LINE_HEIGHT = 1.5;

export const MIN_FONT_SIZE = 1;
export const MAX_FONT_SIZE = 200;

// ── Normalizers (the WRITE lane) ─────────────────────────────────────────────

/**
 * Three states, because a writer needs all three and `T | undefined` carries
 * only two. That missing distinction is exactly how `font: null` came to
 * discard an entire canvas while `color: null` stayed harmless.
 *
 * `value` is the NORMALIZED value and the raw input is not exposed on the `ok`
 * branch — so "validate one string, write another" is structurally impossible
 * rather than merely discouraged.
 */
export type StyleValue<T> =
  | { kind: "absent" }
  | { kind: "ok"; value: T }
  | { kind: "invalid"; detail: string };

const ABSENT: StyleValue<never> = { kind: "absent" };
const okValue = <T>(value: T): StyleValue<T> => ({ kind: "ok", value });
/** Reports the ORIGINAL spelling: a model told `H2` is invalid fixes it faster
 *  than one told `h2` is. Capped like every other error detail in the DSL. */
const invalidValue = (raw: unknown): StyleValue<never> => ({
  kind: "invalid",
  detail: String(raw).slice(0, 60),
});

/** The truthy shape `color` has always used. A model that emits `null` or `""`
 *  for "leave this alone" must not be punished for it. */
const isUnset = (v: unknown): boolean => v === undefined || v === null || v === "";

/**
 * Closed sets match case-insensitively on the trimmed value. `trim` and
 * `toLowerCase` can only remove or fold characters, never introduce one, and
 * the result is compared against an exact ASCII allow-list — so nothing can be
 * folded INTO a set it does not already belong to.
 */
const closedSet =
  <T extends string>(set: readonly T[]) =>
  (v: unknown): StyleValue<T> => {
    if (isUnset(v)) return ABSENT;
    if (typeof v !== "string") return invalidValue(v);
    const folded = v.trim().toLowerCase();
    return (set as readonly string[]).includes(folded)
      ? okValue(folded as T)
      : invalidValue(v);
  };

export const normalizeAlign = closedSet(ALIGNS);
export const normalizeMediaAlign = closedSet(MEDIA_ALIGNS);
export const normalizeHeadingLevel = closedSet(HEADING_LEVELS);
export const normalizeTextTransform = closedSet(TEXT_TRANSFORMS);
export const normalizeFontStyle = closedSet(FONT_STYLES);

/**
 * A plain decimal only — deliberately NOT `parseFloat`/`Number` on the raw
 * value: `parseFloat("16px;color:red")` is `16`, which would wave a payload
 * through as a success. `Number("")`/`Number(" ")` are `0`, `Number("0x10")` is
 * 16. The anchored regex is the guard; `Number()` runs only on a string it has
 * already proved is a plain decimal.
 */
const DECIMAL_RE = /^-?\d+(?:\.\d+)?$/;

/**
 * `max` CLAMPS, `min` REJECTS — that asymmetry is the policy. Clamping is only
 * allowed where the clamped value still reads as the author's intent, and the
 * read lane forces it: `craftJsToBlocks` reports a live `fontSize` verbatim, so
 * a canvas holding 250 must stay AI-editable (clamp), while `0` is unreachable
 * from a read and means invisible text (reject).
 *
 * `zeroIsUnset` is only for props where `0` is a DOCUMENTED sentinel for the
 * element default — letterSpacing and lineHeight, never font size.
 */
export const numberInRange =
  (opts: { min: number; max: number; zeroIsUnset?: boolean }) =>
  (v: unknown): StyleValue<number> => {
    if (isUnset(v)) return ABSENT;
    let n: number;
    if (typeof v === "number") n = v;
    else if (typeof v === "string" && DECIMAL_RE.test(v.trim())) n = Number(v.trim());
    else return invalidValue(v);
    if (!Number.isFinite(n)) return invalidValue(v);
    if (n === 0 && opts.zeroIsUnset) return ABSENT;
    if (n < opts.min) return invalidValue(v);
    return okValue(Math.min(opts.max, n));
  };

export const normalizeFontSize = numberInRange({
  min: MIN_FONT_SIZE,
  max: MAX_FONT_SIZE,
});
/**
 * The block-geometry numbers. They were the last fields still on bare
 * `Math.max/min`, where a model-supplied string yields NaN — which
 * `JSON.stringify` writes as `null`, so the compiler reported ok and the mail
 * carried `<img width="0">` (the picture simply gone) or a spacer collapsed to
 * nothing. A range clamp is not a type guard.
 */
export const normalizeImageWidth = numberInRange({ min: 1, max: 100 });
export const normalizeSpacerHeight = numberInRange({ min: 1, max: 200 });
export const normalizeColumnWidth = numberInRange({ min: 1, max: 12 });

/**
 * −99…99 px: exactly the range of the inspector's letter-spacing field
 * (`SIZE_BOUNDS.letterSpacing`; builder-style-values.test.ts pins the two
 * together). It was −10…20 while the panel was a dropdown that ended at 10.
 * The reader reports `letterSpacing` verbatim, so a writer range narrower
 * than what the panel can store would hand the model a value it is then
 * refused for echoing — the read/write invariant, as for `lineHeight` below.
 */
export const LETTER_SPACING_MIN = -99;
export const LETTER_SPACING_MAX = 99;
export const normalizeLetterSpacing = numberInRange({
  min: LETTER_SPACING_MIN,
  max: LETTER_SPACING_MAX,
  zeroIsUnset: true,
});
/**
 * `min` is 0.1, well below the 0.5 the inspector's line-height field starts
 * at, because the PRESETS author below it: `presets/corporate.ts` sets 0.4 on a decorative
 * `&ldquo;` glyph, `speaker.ts` sets 0.8, four sites set 0.6. The reader reports
 * `lineHeight` verbatim, so a floor above the corpus made the writer reject a
 * value the reader had just handed the model — a dead end no retry could clear,
 * because re-reading returns the same value. That is the read/write invariant,
 * not a taste call.
 *
 * Negative still rejects even though it could never reach the output: both
 * emission sites gate on `> 0`, so a stored negative would do nothing, and
 * reporting success on a write with no effect is the same dishonesty.
 */
export const normalizeLineHeight = numberInRange({
  min: 0.1,
  max: 3,
  zeroIsUnset: true,
});

/**
 * `"` → `'` before validating. Both are valid CSS string delimiters, so the two
 * spellings are the SAME value — and a model writes CSS the way CSS is normally
 * written (`"Helvetica Neue", Arial`). The swap can only rescue a string whose
 * ONLY violating character was `"`, i.e. exactly the legitimate double-quoted
 * stacks; anything carrying `;` `<` `>` `=` `(` `)` `{` `}` `\` `:` still fails
 * the allow-list.
 */
export const normalizeFontStack = (v: unknown): StyleValue<string> => {
  if (isUnset(v)) return ABSENT;
  if (typeof v !== "string") return invalidValue(v);
  const swapped = v.trim().replace(/"/g, "'");
  return isSafeFontStack(swapped) ? okValue(swapped) : invalidValue(v);
};

const HEX6 = /^#[0-9a-fA-F]{6}$/;
const HEX3 = /^#[0-9a-fA-F]{3}$/;

/**
 * `#fff` → `#ffffff`: unambiguously the same colour, and the 6-digit output is
 * what the read lane's `hex()` can report back. Named colours stay OUT for that
 * same reason — the reader cannot report them, so accepting them would break
 * the read/write invariant. This is the MODEL-facing gate; the renderer-side
 * fallback is deliberately more tolerant (see `isRenderableColor`).
 */
export const normalizeColor = (v: unknown): StyleValue<string> => {
  if (isUnset(v)) return ABSENT;
  if (typeof v !== "string") return invalidValue(v);
  const s = v.trim();
  if (HEX6.test(s)) return okValue(s);
  if (HEX3.test(s)) return okValue(`#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`);
  return invalidValue(v);
};

/**
 * The RENDERER's notion of an acceptable colour, deliberately wider than
 * `normalizeColor`: `ColorSettingsA4` emits 8-digit `#RRGGBBAA` when opacity is
 * below 100 and the literal `"transparent"` for its clear button. A gate that
 * only took 6-digit hex would reject values a real user produces through the
 * settings panel.
 */
const HEX_ANY = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * `rgb()`/`rgba()` are here because the A4 picker MINTS them: 8-digit hex is
 * banned in email (Word's engine can't parse it), so `composePickerColor` emits
 * `rgba(r,g,b,a)` for every opacity between 0 and 100 — and 81 of them sit in
 * the shipped preset corpus, in both the compact and the spaced spelling. Until
 * this accepted them, choosing 50% opacity on the link colour silently reverted
 * the sent mail to the default.
 *
 * The accepted ALPHABET is `#`, hex digits, `r g b a`, `(`, `)`, `,`, `.`, the
 * plain space, and the letters of `transparent`. It contains none of
 * `" ; < > / * \ : @ &` or a newline, so neither `</style>` nor a comment nor a
 * second declaration is spellable — and `url(`, `var(`, `color-mix(` are not
 * merely rejected but unspellable. That matters because one of the two sinks is
 * a declaration inside the head `<style>` BLOCK, where a `{` would close the
 * rule and open an attacker-chosen selector.
 *
 * Out on purpose: named colours (the read lane can't report one, so they'd never
 * survive a round trip), `hsl()` and the space-separated `rgb(0 0 0 / 50%)` form
 * (legal CSS that no builder surface emits and Outlook drops — and the slash
 * would cost the one character that turns an adjacent `*` into a comment
 * opener). Channels are 1-3 digits rather than range-checked: every renderer
 * clamps an out-of-range channel, so a range check would only add false
 * rejects to a predicate whose job is INERTNESS, not CSS validity.
 */
const RGB_FN = /^rgba?\( *\d{1,3} *, *\d{1,3} *, *\d{1,3} *(?:, *\d+(?:\.\d+)? *)?\)$/;

export const isRenderableColor = (v: unknown): v is string => {
  if (typeof v !== "string") return false;
  const s = v.trim();
  return s === "transparent" || HEX_ANY.test(s) || RGB_FN.test(s);
};

/**
 * Renderer-side sheet/content width. Clamps rather than rejects because `0` is
 * USER-REACHABLE: `NumberSettings` lets a cleared field through and `Number("")`
 * is `0`, which then persists.
 */
export const MIN_CONTENT_WIDTH = 1;
export const MAX_CONTENT_WIDTH = 5000;
export const renderableWidth = (v: unknown, fallback: number): number => {
  // A NUMERIC STRING is accepted, through the same anchored decimal guard the
  // write lane uses — never `parseFloat`, which would wave `"600px;color:red"`
  // through as 600. `NumberSettings` writes `Number(value)`, but the width also
  // arrives from stored records and from callers that stringify, and a stored
  // `"800"` used to fall silently to the 600 default instead of being honoured.
  const n =
    typeof v === "number"
      ? v
      : typeof v === "string" && DECIMAL_RE.test(v.trim())
        ? Number(v.trim())
        : NaN;
  return Number.isFinite(n) && n > 0
    ? Math.max(MIN_CONTENT_WIDTH, Math.min(MAX_CONTENT_WIDTH, n))
    : fallback;
};
